import { supabase } from '@/lib/supabase';
import { createCustomer } from '@/services/billing';
import { trackEvent } from '@/lib/analytics';

export interface Project {
  id: string;
  name: string;
  description: string | null;
  owner_id: string;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

export async function listProjects(): Promise<{
  data: Project[] | null;
  error: Error | null;
}> {
  const { data, error } = await supabase
    .from('projects')
    .select('id, name, description, owner_id, archived_at, created_at, updated_at')
    .order('name');

  return { data, error };
}

export async function getProject(
  projectId: string,
): Promise<{ data: Project | null; error: Error | null }> {
  const { data, error } = await supabase
    .from('projects')
    .select('*')
    .eq('id', projectId)
    .single();

  return { data, error };
}

export async function createProject(
  input: { name: string; description: string },
): Promise<{ data: { id: string } | null; error: Error | null }> {
  if (!input.name || input.name.trim().length === 0) {
    return { data: null, error: new Error('Project name is required') };
  }
  if (!input.description || input.description.trim().length < 10) {
    return {
      data: null,
      error: new Error('Description must be at least 10 characters'),
    };
  }

  const { data, error } = await supabase.rpc('create_project', {
    p_name: input.name.trim(),
    p_description: input.description.trim(),
  });

  if (error) return { data: null, error };

  const projectId = data as string;

  // Provision free trial subscription (best-effort — project is created even if this fails)
  createCustomer(projectId).catch((err) => {
    console.error('Failed to provision trial subscription:', err);
  });

  // Trigger background init job for Spec-Kit, Mem0, and Beads (fire-and-forget)
  supabase.functions.invoke('project-init', {
    body: { project_id: projectId },
  }).catch((err) => {
    console.error('Failed to trigger project init job:', err);
  });

  // Analytics: track first project creation milestone (fire-and-forget)
  try {
    if (!localStorage.getItem('synter_first_project')) {
      trackEvent('first_project_created');
      localStorage.setItem('synter_first_project', '1');
    }
  } catch {
    // localStorage or analytics unavailable — ignore
  }

  return { data: { id: projectId }, error: null };
}

export async function updateProject(
  projectId: string,
  updates: { name?: string; description?: string },
): Promise<{ error: Error | null }> {
  const { error } = await supabase
    .from('projects')
    .update(updates)
    .eq('id', projectId);

  return { error };
}

export async function archiveProject(
  projectId: string,
): Promise<{ error: Error | null }> {
  const { error } = await supabase.rpc('archive_project', {
    p_project_id: projectId,
  });

  return { error };
}

export async function unarchiveProject(
  projectId: string,
): Promise<{ error: Error | null }> {
  const { error } = await supabase.rpc('unarchive_project', {
    p_project_id: projectId,
  });

  return { error };
}
